'use client';

import { AfibShopifyMap } from 'content/products/addons/afib';
import { CardioAdaptibilityShopifyMap } from 'content/products/addons/cardioAdaptibility';
import { CnoProShopifyMap } from 'content/products/addons/cnoPro';
import { CnoPlusShopifyMap } from 'content/products/addons/cnoPlus';
import { ClueShopifyMap } from 'content/products/addons/clue';
import {
  productMap_diesel_ring,
  DieselRingSizes,
  DieselRingType,
  DIESEL_RING_STATIC_SHIPPING,
} from 'content/products/dieselRing';
import { SizingKitShopifyMap } from 'content/products/ring';
import { UhxShopifyMap } from 'content/products/uhx';
import {
  AddonsAction,
  AddonsState,
  CheckoutParams,
  DieselRingContextType,
} from './types';

import { ReactNode, useRef, useEffect } from 'react';
import styles from './checkout.module.css';

import { addToCartArgsType, ProductType } from 'components/shared/Cart/types';
import { tagBundle } from 'components/shared/Cart/bundle';
import { Currency } from 'utils/Currency';

// Simplified checkout line items - no charger, no engraving
export function getCheckoutLineItems(
  params: CheckoutParams,
): addToCartArgsType[] {
  const {
    color,
    size,
    store,
    eSizingSize,
    uhxCart,
    afibCart,
    cardioAdaptibilityCart,
    cnoProCart,
    clueCart,
    isCnOPlus,
  } = params;

  if (!color || !size || !store) return [];

  const productNode = productMap_diesel_ring[color]?.[size];
  if (!productNode) return [];

  // Diesel Ring only has NOT_ENGRAVED type
  const mainVariantId =
    productNode[DieselRingType.NOT_ENGRAVED]?.variantId[store];
  if (!mainVariantId) return [];

  // Build noteAttributes for eSizing
  const noteAttributes: Array<{ key: string; value: string }> = [];
  if (eSizingSize) noteAttributes.push({ key: 'eSizing', value: 'true' });

  // Start with the ring line item
  const lineItems: addToCartArgsType[] = [];

  // Add a Sizing Kit if "open" size is chosen
  if (
    size === DieselRingSizes.OPEN &&
    SizingKitShopifyMap[store]?.shopifyVariantId
  ) {
    lineItems.push({
      shopifyVariantId: SizingKitShopifyMap[store].shopifyVariantId as string,
      productType: ProductType.RING,
    });
  }

  // No travel charger for Diesel Ring - removed

  // Add UHX addon if selected
  if (uhxCart && UhxShopifyMap[store]?.[uhxCart]?.variantId) {
    lineItems.push({
      shopifyVariantId: UhxShopifyMap[store][uhxCart].variantId as string,
      productType: ProductType.GENERIC,
    });
  }

  // Add Cardio Adaptibility addon if selected
  if (
    cardioAdaptibilityCart &&
    CardioAdaptibilityShopifyMap[store]?.[cardioAdaptibilityCart]?.variantId
  ) {
    lineItems.push({
      shopifyVariantId: CardioAdaptibilityShopifyMap[store][
        cardioAdaptibilityCart
      ].variantId as string,
      productType: ProductType.RING,
    });
  }

  // Add Afib addon if selected
  if (afibCart && AfibShopifyMap[store]?.[afibCart]?.variantId) {
    lineItems.push({
      shopifyVariantId: AfibShopifyMap[store][afibCart].variantId as string,
      productType: ProductType.RING,
    });
  }

  // Add CNO Pro/Plus addon if selected
  if (cnoProCart) {
    const cnoMap = isCnOPlus ? CnoPlusShopifyMap : CnoProShopifyMap;
    if (cnoMap[store]?.[cnoProCart]?.variantId) {
      lineItems.push({
        shopifyVariantId: cnoMap[store][cnoProCart].variantId as string,
        productType: ProductType.RING,
      });
    }
  }

  if (clueCart === 'Year1' && ClueShopifyMap[store]?.Year1?.variantId) {
    lineItems.push({
      shopifyVariantId: ClueShopifyMap[store].Year1.variantId as string,
      productType: ProductType.RING,
    });
  }

  // DISABLED: Respiratory Health PowerPlug — re-enable by uncommenting
  // // Add Respiratory addon if selected
  // if (
  //   respiratoryCart === 'Year1' &&
  //   RespiratoryShopifyMap[store]?.Year1?.variantId
  // ) {
  //   lineItems.push({
  //     shopifyVariantId: RespiratoryShopifyMap[store].Year1.variantId as string,
  //     productType: ProductType.RING,
  //   });
  // }

  // Add main ring item last
  lineItems.push({
    shopifyVariantId: mainVariantId,
    productType: ProductType.RING_DIESEL,
    params:
      noteAttributes.length > 0
        ? { productType: 'ring_diesel', noteAttributes }
        : { productType: 'ring_diesel' },
  });

  return tagBundle(lineItems);
}

// No fetchEstimatesForStore - Diesel Ring uses static shipping message

export const ShippingLabel = ({
  className,
  content,
  justContent = false,
}: {
  className?: string;
  content: ReactNode;
  justContent?: boolean;
}) => {
  const renderCount = useRef(0);
  useEffect(() => {
    renderCount.current += 1;
  }, [content]);

  const rootClassName = className ? `${styles.root} ${className}` : styles.root;

  if (justContent) {
    return (
      <span key={renderCount.current} className={rootClassName}>
        {content}
      </span>
    );
  }

  return (
    <span className={rootClassName} key={renderCount.current}>
      <span>Dispatched by : {content}</span>
    </span>
  );
};

// Static shipping estimate for Diesel Ring
export function getShippingEstimate() {
  return (
    <ShippingLabel
      content={
        <strong style={{ fontWeight: '500' }}>
          {DIESEL_RING_STATIC_SHIPPING}
        </strong>
      }
      justContent={true}
    />
  );
}

export const initialDieselRingSelectionContext: DieselRingContextType = {
  size: DieselRingSizes.OPEN,
  setSize: () => undefined,
  setColor: () => undefined,
  // No chargerType - removed for Diesel Ring
  defaultCurrency: Currency.inr,
  // No engraving - removed for Diesel Ring
  uhx: false,
  setUhx: () => undefined,
  uhxCart: null,
  setUhxCart: () => undefined,
  afibCart: null,
  setAfibCart: () => undefined,
  clueCart: null,
  setClueCart: () => undefined,
  cardioAdaptibilityCart: null,
  setCardioAdaptabilityCart: () => undefined,
  cnoProCart: null,
  setCnoProCart: () => undefined,
  respiratoryCart: null,
  setRespiratoryCart: () => undefined,
  checkout: async () => undefined,
  isLoading: false,
  price: 0,
  ultraHumanPrice: 0,
  effectivePrice: 0,
  // No chargerPrice - removed for Diesel Ring
  shippingEstimate: DIESEL_RING_STATIC_SHIPPING, // Static shipping message
  affiliateDetails: null,
  store: null,
  campaignActive: false,
  affiliateDiscount: 0,
  goToNextStep: () => undefined,
  effectiveDiscount: { type: '', amount: 0 },
  sectionRefs: {},
  // No showEngravingSection - removed for Diesel Ring
  priceDrop: 0,
  notServicable: false,
  notServicableMessage: undefined,
  outOfStock: false,
};

// Simplified addons reducer - no engraving
export function addonsReducer(
  state: AddonsState,
  action: AddonsAction,
): AddonsState {
  switch (action.type) {
    case 'SET_UHX_CART':
      return { ...state, uhxCart: action.payload };
    case 'SET_AFIB_CART':
      return { ...state, afibCart: action.payload };
    case 'SET_CARDIO_ADAPTIBILITY_CART':
      return { ...state, cardioAdaptibilityCart: action.payload };
    case 'SET_CNO_PRO_CART':
      return { ...state, cnoProCart: action.payload };
    case 'SET_CLUE_CART':
      return { ...state, clueCart: action.payload };
    case 'SET_RESPIRATORY_CART':
      return { ...state, respiratoryCart: action.payload };
    default:
      return state;
  }
}

// Simplified initial state - no engraving
export const initialAddonsState: AddonsState = {
  uhxCart: null,
  afibCart: null,
  cardioAdaptibilityCart: null,
  cnoProCart: null,
  clueCart: null,
  respiratoryCart: null,
};
